﻿(() => {
  const DEBUG = new URLSearchParams(window.location.search).get('wallartdebug') === '1';
  const GLOBAL_STATE_KEY = '__wallArtGalleryRendererV1';
  const globalState = window[GLOBAL_STATE_KEY] || (window[GLOBAL_STATE_KEY] = {});
  if (!(globalState.renderTokens instanceof Map)) {
    globalState.renderTokens = new Map();
  }
  if (!(globalState.sectionInstances instanceof Map)) {
    globalState.sectionInstances = new Map();
  }

  const CONFIG_SELECTOR = 'script[id^="WallArtGalleryRendererConfig-"]';
  const SLOT_SELECTORS = [
    '[data-wall-art-dynamic-slot="2"]',
    '[data-wall-art-dynamic-slot="3"]',
    '[data-wall-art-dynamic-slot="4"]',
  ].join(', ');

  const OUTPUT_SIZE = 1200;
  const FRAME_SLICE_PX = 120;
  const FRAME_THICKNESS_PX = 48;
  const VARIANT_CHANGE_DEBOUNCE_MS = 80;
  const IDLE_RENDER_DELAY_MS = 60;
  const MAX_RENDER_CACHE_ENTRIES = 40;
  const DEFAULT_ART_BOX = Object.freeze({ x: 220, y: 120, w: 760, h: 960 });
  const DEFAULT_FRAMED_ART_HERO_BOX = Object.freeze({ x: 220, y: 120, w: 760, h: 960 });

  const imageCache = new Map();
  const renderCache = new Map();
  const frameSliceCache = new Map();

  function clampNumber(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function log(...args) {
    if (!DEBUG) return;
    console.log('[wall-art-gallery-renderer]', ...args);
  }

  function createDebouncedFunction(fn, waitMs) {
    let timerId = null;

    const debounced = (...args) => {
      if (timerId !== null) {
        window.clearTimeout(timerId);
      }

      timerId = window.setTimeout(() => {
        timerId = null;
        fn(...args);
      }, waitMs);
    };

    debounced.cancel = () => {
      if (timerId !== null) {
        window.clearTimeout(timerId);
        timerId = null;
      }
    };

    return debounced;
  }

  function runWhenIdle(task, timeoutMs = 120) {
    if (typeof requestIdleCallback === 'function') {
      return new Promise((resolve) => {
        requestIdleCallback(() => resolve(task()), { timeout: timeoutMs });
      });
    }

    return new Promise((resolve) => {
      window.setTimeout(() => resolve(task()), IDLE_RENDER_DELAY_MS);
    });
  }

  function asNormalizedKey(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/[_-]+/g, ' ');
  }

  function formatNumericToken(value) {
    const num = Number.parseFloat(value);
    if (!Number.isFinite(num)) return String(value || '').trim();
    if (Number.isInteger(num)) return String(num);
    return String(num).replace(/\.0+$/, '');
  }

  function toAbsoluteUrl(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';

    try {
      return new URL(raw, window.location.href).href;
    } catch (_error) {
      return raw;
    }
  }

  function isSameOriginUrl(url) {
    const resolvedUrl = toAbsoluteUrl(url);
    if (!resolvedUrl) return false;

    try {
      return new URL(resolvedUrl).origin === window.location.origin;
    } catch (_error) {
      return false;
    }
  }

  function pushUniqueUrl(target, url) {
    const resolvedUrl = toAbsoluteUrl(url);
    if (!resolvedUrl) return;
    if (!target.includes(resolvedUrl)) {
      target.push(resolvedUrl);
    }
  }

  function normalizeArtworkAspectKey(value) {
    const key = String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[:\-/\s]+/g, '_');
    if (key === '2_3' || key === '3_4' || key === '4_5') return key;
    return '';
  }

  function getAspectKeyFromSize(sizeValue) {
    const normalizedSize = normalizeSize(sizeValue);
    switch (normalizedSize) {
      case '8x10':
      case '10x8':
      case '16x20':
      case '20x16':
        return '4_5';
      case '18x24':
      case '24x18':
        return '3_4';
      case '12x18':
      case '18x12':
      case '20x30':
      case '30x20':
      case '24x36':
      case '36x24':
      case '16x24':
      case '24x16':
      case '32x48':
      case '48x32':
      case '40x60':
      case '60x40':
        return '2_3';
      default:
        return '';
    }
  }

  function getAspectKeyFromRatio(ratioValue) {
    const parsedRatio = Number.parseFloat(ratioValue);
    if (!Number.isFinite(parsedRatio) || parsedRatio <= 0) return '';

    const portraitRatio = parsedRatio > 1 ? 1 / parsedRatio : parsedRatio;
    const candidates = [
      { key: '2_3', ratio: 2 / 3 },
      { key: '3_4', ratio: 3 / 4 },
      { key: '4_5', ratio: 4 / 5 },
    ];

    let best = candidates[0];
    let bestDelta = Math.abs(portraitRatio - best.ratio);
    for (let i = 1; i < candidates.length; i += 1) {
      const delta = Math.abs(portraitRatio - candidates[i].ratio);
      if (delta < bestDelta) {
        best = candidates[i];
        bestDelta = delta;
      }
    }

    return best.key;
  }

  function resolveArtworkAspectKey({ variant, size, aspectRatio }) {
    const fromVariant = normalizeArtworkAspectKey(variant?.aspectKey);
    if (fromVariant) return fromVariant;

    const fromSize = getAspectKeyFromSize(size || variant?.size);
    if (fromSize) return fromSize;

    const ratioFromSize = parseSizeToRatio(size || variant?.size);
    const fromSizeRatio = getAspectKeyFromRatio(ratioFromSize);
    if (fromSizeRatio) return fromSizeRatio;

    const fromAspectRatio = getAspectKeyFromRatio(aspectRatio ?? variant?.aspectRatio);
    if (fromAspectRatio) return fromAspectRatio;

    return '2_3';
  }

  function buildArtworkCandidateUrls(config, context = {}) {
    const orderedUrls = [];

    const normalizedProductType = normalizeProductType(context?.productType || context?.variant?.productType);
    const shouldUseAspectArtwork =
      normalizedProductType === 'art_print' || normalizedProductType === 'framed_art_print';
    if (shouldUseAspectArtwork) {
      const aspectKey = resolveArtworkAspectKey(context);
      const variantAspectArtworkUrl = context?.variant?.artworkUrlsByAspect?.[aspectKey];
      const productAspectArtworkUrl = config?.artworkUrlsByAspect?.[aspectKey];
      pushUniqueUrl(orderedUrls, variantAspectArtworkUrl);
      pushUniqueUrl(orderedUrls, productAspectArtworkUrl);
      return orderedUrls;
    }

    // Canvas types (canvas_print, framed_canvas_print) ship 2x3 only,
    // BUT the variant the caller passes still carries per-aspect URLs
    // (via the gallery builder's synthVariant or the product page's
    // own variant data). We have to check the variant FIRST: on the
    // gallery-builder page, `config.artworkUrlsByAspect` is empty
    // (it's a per-page config, not per-design), so skipping the variant
    // would fail.
    const variantDefaultArtworkUrl =
      context?.variant?.artworkUrlsByAspect?.['2_3'] ||
      context?.variant?.artworkUrl;
    const configDefaultArtworkUrl =
      config?.artworkUrlsByAspect?.['2_3'] ||
      config?.artworkUrl;
    pushUniqueUrl(orderedUrls, variantDefaultArtworkUrl);
    pushUniqueUrl(orderedUrls, configDefaultArtworkUrl);
    return orderedUrls;
  }

  function normalizeProductType(value) {
    const key = asNormalizedKey(value).replace(/\s+/g, ' ');

    if (!key) return 'art_print';

    if (
      key === 'framed_canvas_print' ||
      key === 'framed canvas' ||
      key === 'framed canvas print' ||
      key === 'framed canvas prints' ||
      key === 'floating frame canvas' ||
      key === 'float frame canvas' ||
      key.includes('framed canvas')
    ) {
      return 'framed_canvas_print';
    }

    if (
      key === 'framed_art_print' ||
      key === 'framed art print' ||
      key === 'framed art prints' ||
      key === 'framed poster' ||
      key === 'framed posters' ||
      key === 'framed print' ||
      key === 'framed prints' ||
      key.includes('framed art print') ||
      key.includes('framed poster') ||
      key.includes('framed print')
    ) {
      return 'framed_art_print';
    }

    if (
      key === 'canvas_print' ||
      key === 'canvas' ||
      key === 'canvas print' ||
      key === 'canvas prints' ||
      key === 'gallery wrapped canvas' ||
      key.includes('canvas print')
    ) {
      return 'canvas_print';
    }

    if (
      key === 'art_print' ||
      key === 'art print' ||
      key === 'art prints' ||
      key === 'poster' ||
      key === 'posters' ||
      key === 'print' ||
      key === 'prints' ||
      key === 'unframed art print' ||
      key.includes('art print')
    ) {
      return 'art_print';
    }

    return 'art_print';
  }

  function isFramedProductType(productType) {
    return productType === 'framed_art_print' || productType === 'framed_canvas_print';
  }

  function normalizeFrameColor(value) {
    const key = asNormalizedKey(value);

    if (!key || key === 'none' || key === 'no frame' || key === 'unframed') return 'unframed';
    if (key === 'black' || key === 'black frame' || key.includes('black')) return 'black';
    if (key === 'white' || key === 'white frame' || key.includes('white')) return 'white';

    if (
      key === 'natural' ||
      key === 'natural frame' ||
      key === 'oak' ||
      key === 'wood' ||
      key === 'wood frame' ||
      key.includes('natural') ||
      key.includes('oak') ||
      key.includes('wood')
    ) {
      return 'natural';
    }

    return 'unframed';
  }

  function normalizeSize(value) {
    const original = String(value ?? '').trim();
    if (!original) return original;

    const sanitized = original
      .toLowerCase()
      .replace(/inches|inch|in\.|in/g, '')
      .replace(/"/g, '')
      .replace(/×/g, 'x')
      .replace(/\s+/g, ' ')
      .trim();

    const match = sanitized.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/i);
    if (!match) return original;

    const width = formatNumericToken(match[1]);
    const height = formatNumericToken(match[2]);
    if (!width || !height) return original;

    return `${width}x${height}`;
  }

  function parseSizeToRatio(sizeString) {
    const normalized = normalizeSize(sizeString);
    const match = String(normalized || '').match(/^(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)$/i);
    if (!match) return null;

    const width = Number.parseFloat(match[1]);
    const height = Number.parseFloat(match[2]);
    if (!Number.isFinite(width) || !Number.isFinite(height) || height <= 0) return null;

    return width / height;
  }

  function isAllowedSizeForProductType(productType, size, config) {
    const allowedMap = config?.allowedSizesByProductType;
    if (!allowedMap || typeof allowedMap !== 'object') return true;

    const normalizedProductType = normalizeProductType(productType);
    const allowedSizes = allowedMap[normalizedProductType];
    if (!Array.isArray(allowedSizes) || !allowedSizes.length) return true;

    const normalizedCandidate = normalizeSize(size);
    return allowedSizes.some((allowedSize) => normalizeSize(allowedSize) === normalizedCandidate);
  }

  function normalizeSizeGroup(value) {
    const key = asNormalizedKey(value);
    if (key === 'small') return 'small';
    if (key === 'medium') return 'medium';
    if (key === 'large') return 'large';
    return '';
  }

  function getAllowedSizesBySizeGroup(config) {
    const configMap = config?.allowedSizesBySizeGroup;
    if (configMap && typeof configMap === 'object') {
      return {
        small: Array.isArray(configMap.small) ? configMap.small : [],
        medium: Array.isArray(configMap.medium) ? configMap.medium : [],
        large: Array.isArray(configMap.large) ? configMap.large : [],
      };
    }

    return {
      small: ['8x10', '12x18'],
      medium: ['16x20', '18x24', '20x30', '24x36'],
      large: ['32x48', '40x60'],
    };
  }

  function inferSizeGroupFromDimensions(size) {
    const normalizedSize = normalizeSize(size);
    const match = String(normalizedSize || '').match(/^(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)$/i);
    if (!match) return 'medium';

    const width = Number.parseFloat(match[1]);
    const height = Number.parseFloat(match[2]);
    if (!Number.isFinite(width) || !Number.isFinite(height)) return 'medium';

    const longestEdge = Math.max(width, height);
    const shortestEdge = Math.min(width, height);

    if (longestEdge >= 48 || shortestEdge >= 32) return 'large';
    if (shortestEdge <= 12) return 'small';
    return 'medium';
  }

  function getSizeGroupForSize(size, config) {
    const normalizedSize = normalizeSize(size);
    const allowedByGroup = getAllowedSizesBySizeGroup(config);

    for (const groupKey of ['small', 'medium', 'large']) {
      const sizes = allowedByGroup[groupKey] || [];
      const found = sizes.some((allowedSize) => normalizeSize(allowedSize) === normalizedSize);
      if (found) return groupKey;
    }

    return inferSizeGroupFromDimensions(normalizedSize);
  }

  function getRenderCacheKey({
    productId,
    variantId,
    slot,
    backgroundKey,
    productType,
    frameColor,
    frameThickness,
    size,
    sizeGroup,
    templateVersion,
    placementSig,
    shadowSig,
  }) {
    return [
      String(productId ?? ''),
      String(variantId ?? ''),
      String(slot ?? ''),
      String(backgroundKey ?? ''),
      String(productType ?? ''),
      String(frameColor ?? ''),
      String(frameThickness ?? ''),
      String(size ?? ''),
      String(sizeGroup ?? ''),
      String(templateVersion ?? 'v1'),
      String(placementSig ?? ''),
      String(shadowSig ?? ''),
    ].join('|');
  }

  function getPlacementSignature(config, backgroundKey, sizeGroup) {
    const a = getAnchorPlacement(config, backgroundKey, sizeGroup);
    const s = getAnchorSize(config, sizeGroup);
    return `${a.x},${a.y},${a.w},${a.h}|${s?.w ?? ''}x${s?.h ?? ''}`;
  }

  function getShadowProfile(config, productType) {
    const configured = config?.shadowByProductType?.[productType];
    const toNumber = (value, fallback) => {
      const parsed = Number.parseFloat(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    };

    return {
      distanceScale: clampNumber(toNumber(configured?.distanceScale, 100), 0, 300) / 100,
      directionX: clampNumber(toNumber(configured?.directionX, 80), -300, 300) / 100,
      directionY: clampNumber(toNumber(configured?.directionY, 115), -300, 300) / 100,
      blurScale: clampNumber(toNumber(configured?.blurScale, 100), 0, 300) / 100,
      castTrailScale: clampNumber(toNumber(configured?.castTrailScale, 100), 0, 300) / 100,
      directionalStrengthScale: clampNumber(toNumber(configured?.directionalStrengthScale, 100), 0, 300) / 100,
      contactStrengthScale: clampNumber(toNumber(configured?.contactStrengthScale, 100), 0, 300) / 100,
      opacityScale: clampNumber(toNumber(configured?.opacityScale, 100), 0, 300) / 100,
    };
  }

  function getShadowSignature(config, productType) {
    const profile = getShadowProfile(config, productType);
    return [
      profile.distanceScale,
      profile.directionX,
      profile.directionY,
      profile.blurScale,
      profile.castTrailScale,
      profile.directionalStrengthScale,
      profile.contactStrengthScale,
      profile.opacityScale,
    ].join(',');
  }

  function loadImage(url) {
    const resolvedUrl = String(url || '').trim();
    if (!resolvedUrl) {
      return Promise.reject(new Error('loadImage requires a non-empty URL.'));
    }

    if (imageCache.has(resolvedUrl)) {
      return imageCache.get(resolvedUrl);
    }

    const promise = new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = () => {
        imageCache.delete(resolvedUrl);
        reject(new Error(`Failed to load image: ${resolvedUrl}`));
      };
      image.src = resolvedUrl;
    });

    imageCache.set(resolvedUrl, promise);
    return promise;
  }

  function canvasToBlobUrl(canvas, type = 'image/webp', quality = 0.92) {
    return new Promise((resolve, reject) => {
      if (!(canvas instanceof HTMLCanvasElement) || typeof canvas.toBlob !== 'function') {
        reject(new Error('canvasToBlobUrl requires a valid canvas element.'));
        return;
      }

      const finalize = (blob) => {
        if (blob) {
          resolve(URL.createObjectURL(blob));
          return;
        }

        if (type !== 'image/png') {
          canvas.toBlob((pngBlob) => {
            if (pngBlob) {
              resolve(URL.createObjectURL(pngBlob));
            } else {
              reject(new Error('Failed to convert canvas to blob URL.'));
            }
          }, 'image/png');
          return;
        }

        reject(new Error('Failed to convert canvas to blob URL.'));
      };

      canvas.toBlob(finalize, type, quality);
    });
  }

  function revokeOldBlobUrl(img) {
    const blobUrl = img?.dataset?.wallArtBlobUrl;
    if (!blobUrl) return;

    try {
      URL.revokeObjectURL(blobUrl);
    } catch (_error) {
      // Ignore invalid blob URL revoke errors.
    }

    delete img.dataset.wallArtBlobUrl;
  }

  function revokeBlobUrl(blobUrl) {
    if (!blobUrl) return;
    try {
      URL.revokeObjectURL(blobUrl);
    } catch (_error) {
      // Ignore invalid blob URL revoke errors.
    }
  }

  function isBlobUrlUsedInDocument(blobUrl) {
    if (!blobUrl) return false;

    const renderedImages = document.querySelectorAll('img[data-wall-art-blob-url]');
    for (const image of renderedImages) {
      if (image.dataset.wallArtBlobUrl === blobUrl || image.currentSrc === blobUrl || image.src === blobUrl) {
        return true;
      }
    }

    return false;
  }

  function setRenderCacheEntry(renderCacheKey, blobUrl) {
    if (!renderCacheKey || !blobUrl) return;

    if (renderCache.has(renderCacheKey)) {
      renderCache.delete(renderCacheKey);
    }

    renderCache.set(renderCacheKey, {
      blobUrl,
      createdAt: Date.now(),
      renderCacheKey,
    });

    while (renderCache.size > MAX_RENDER_CACHE_ENTRIES) {
      const oldestKey = renderCache.keys().next().value;
      const oldestEntry = renderCache.get(oldestKey);
      renderCache.delete(oldestKey);

      const oldestBlobUrl = oldestEntry?.blobUrl;
      if (oldestBlobUrl && !isBlobUrlReferencedInRenderCache(oldestBlobUrl) && !isBlobUrlUsedInDocument(oldestBlobUrl)) {
        revokeBlobUrl(oldestBlobUrl);
      }
    }
  }

  async function loadFirstAvailableImage(urls) {
    const candidates = Array.isArray(urls) ? urls : [];
    let lastError = null;

    for (const candidateUrl of candidates) {
      const url = String(candidateUrl || '').trim();
      if (!url) continue;

      try {
        const image = await loadImage(url);
        return { image, usedUrl: url };
      } catch (error) {
        lastError = error;
      }
    }

    if (lastError) throw lastError;
    throw new Error('No artwork image URL candidates were available.');
  }

  function drawImageCover(ctx, image, x, y, w, h) {
    if (!ctx || !image || w <= 0 || h <= 0) return;

    const sourceWidth = image.naturalWidth || image.width;
    const sourceHeight = image.naturalHeight || image.height;
    if (!sourceWidth || !sourceHeight) return;

    const scale = Math.max(w / sourceWidth, h / sourceHeight);
    const drawWidth = sourceWidth * scale;
    const drawHeight = sourceHeight * scale;
    const drawX = x + (w - drawWidth) / 2;
    const drawY = y + (h - drawHeight) / 2;

    ctx.drawImage(image, drawX, drawY, drawWidth, drawHeight);
  }

  function drawImageContain(ctx, image, x, y, w, h) {
    if (!ctx || !image || w <= 0 || h <= 0) return;

    const sourceWidth = image.naturalWidth || image.width;
    const sourceHeight = image.naturalHeight || image.height;
    if (!sourceWidth || !sourceHeight) return;

    const scale = Math.min(w / sourceWidth, h / sourceHeight);
    const drawWidth = sourceWidth * scale;
    const drawHeight = sourceHeight * scale;
    const drawX = x + (w - drawWidth) / 2;
    const drawY = y + (h - drawHeight) / 2;

    ctx.drawImage(image, drawX, drawY, drawWidth, drawHeight);
  }

  function drawNineSliceImage(ctx, image, src, dest) {
    if (!ctx || !image || !src || !dest) return;
    if (src.w <= 0 || src.h <= 0 || dest.w <= 0 || dest.h <= 0) return;

    let srcLeft = Math.max(0, Math.min(src.sliceLeft || 0, src.w));
    let srcRight = Math.max(0, Math.min(src.sliceRight || 0, src.w));
    let srcTop = Math.max(0, Math.min(src.sliceTop || 0, src.h));
    let srcBottom = Math.max(0, Math.min(src.sliceBottom || 0, src.h));

    if (srcLeft + srcRight > src.w) {
      const scale = src.w / (srcLeft + srcRight);
      srcLeft *= scale;
      srcRight *= scale;
    }
    if (srcTop + srcBottom > src.h) {
      const scale = src.h / (srcTop + srcBottom);
      srcTop *= scale;
      srcBottom *= scale;
    }

    let destLeft = Math.max(0, Math.min(dest.sliceLeft || 0, dest.w));
    let destRight = Math.max(0, Math.min(dest.sliceRight || 0, dest.w));
    let destTop = Math.max(0, Math.min(dest.sliceTop || 0, dest.h));
    let destBottom = Math.max(0, Math.min(dest.sliceBottom || 0, dest.h));

    if (destLeft + destRight > dest.w) {
      const scale = dest.w / (destLeft + destRight);
      destLeft *= scale;
      destRight *= scale;
    }
    if (destTop + destBottom > dest.h) {
      const scale = dest.h / (destTop + destBottom);
      destTop *= scale;
      destBottom *= scale;
    }

    const sx = [src.x, src.x + srcLeft, src.x + src.w - srcRight, src.x + src.w];
    const sy = [src.y, src.y + srcTop, src.y + src.h - srcBottom, src.y + src.h];
    const dx = [dest.x, dest.x + destLeft, dest.x + dest.w - destRight, dest.x + dest.w];
    const dy = [dest.y, dest.y + destTop, dest.y + dest.h - destBottom, dest.y + dest.h];

    function drawPatch(sx0, sy0, sx1, sy1, dx0, dy0, dx1, dy1) {
      const sw = sx1 - sx0;
      const sh = sy1 - sy0;
      const dw = dx1 - dx0;
      const dh = dy1 - dy0;
      if (sw <= 0 || sh <= 0 || dw <= 0 || dh <= 0) return;
      ctx.drawImage(image, sx0, sy0, sw, sh, dx0, dy0, dw, dh);
    }

    drawPatch(sx[0], sy[0], sx[1], sy[1], dx[0], dy[0], dx[1], dy[1]);
    drawPatch(sx[1], sy[0], sx[2], sy[1], dx[1], dy[0], dx[2], dy[1]);
    drawPatch(sx[2], sy[0], sx[3], sy[1], dx[2], dy[0], dx[3], dy[1]);

    drawPatch(sx[0], sy[1], sx[1], sy[2], dx[0], dy[1], dx[1], dy[2]);
    drawPatch(sx[1], sy[1], sx[2], sy[2], dx[1], dy[1], dx[2], dy[2]);
    drawPatch(sx[2], sy[1], sx[3], sy[2], dx[2], dy[1], dx[3], dy[2]);

    drawPatch(sx[0], sy[2], sx[1], sy[3], dx[0], dy[2], dx[1], dy[3]);
    drawPatch(sx[1], sy[2], sx[2], sy[3], dx[1], dy[2], dx[2], dy[3]);
    drawPatch(sx[2], sy[2], sx[3], sy[3], dx[2], dy[2], dx[3], dy[3]);
  }

  function getFrameSourceSlices(image) {
    const cacheKey = image?.currentSrc || image?.src || `${image?.naturalWidth || 0}x${image?.naturalHeight || 0}`;
    if (frameSliceCache.has(cacheKey)) {
      return frameSliceCache.get(cacheKey);
    }

    const width = image?.naturalWidth || image?.width || 0;
    const height = image?.naturalHeight || image?.height || 0;
    if (width <= 0 || height <= 0) {
      const fallback = {
        top: FRAME_SLICE_PX,
        right: FRAME_SLICE_PX,
        bottom: FRAME_SLICE_PX,
        left: FRAME_SLICE_PX,
      };
      frameSliceCache.set(cacheKey, fallback);
      return fallback;
    }

    const offscreen = document.createElement('canvas');
    offscreen.width = width;
    offscreen.height = height;
    const offscreenCtx = offscreen.getContext('2d', { willReadFrequently: true });
    if (!offscreenCtx) {
      const fallback = {
        top: FRAME_SLICE_PX,
        right: FRAME_SLICE_PX,
        bottom: FRAME_SLICE_PX,
        left: FRAME_SLICE_PX,
      };
      frameSliceCache.set(cacheKey, fallback);
      return fallback;
    }

    offscreenCtx.clearRect(0, 0, width, height);
    offscreenCtx.drawImage(image, 0, 0, width, height);

    const imageData = offscreenCtx.getImageData(0, 0, width, height).data;
    const alphaThreshold = 8;
    const midX = Math.floor(width / 2);
    const midY = Math.floor(height / 2);

    const alphaAt = (x, y) => imageData[(y * width + x) * 4 + 3];

    let leftEdge = -1;
    for (let x = 0; x < width; x += 1) {
      if (alphaAt(x, midY) <= alphaThreshold) {
        leftEdge = x;
        break;
      }
    }

    let rightEdge = -1;
    for (let x = width - 1; x >= 0; x -= 1) {
      if (alphaAt(x, midY) <= alphaThreshold) {
        rightEdge = x;
        break;
      }
    }

    let topEdge = -1;
    for (let y = 0; y < height; y += 1) {
      if (alphaAt(midX, y) <= alphaThreshold) {
        topEdge = y;
        break;
      }
    }

    let bottomEdge = -1;
    for (let y = height - 1; y >= 0; y -= 1) {
      if (alphaAt(midX, y) <= alphaThreshold) {
        bottomEdge = y;
        break;
      }
    }

    const hasDetectedAperture = leftEdge >= 0 && rightEdge >= 0 && topEdge >= 0 && bottomEdge >= 0 && leftEdge < rightEdge && topEdge < bottomEdge;
    if (!hasDetectedAperture) {
      const fallback = {
        top: FRAME_SLICE_PX,
        right: FRAME_SLICE_PX,
        bottom: FRAME_SLICE_PX,
        left: FRAME_SLICE_PX,
      };
      frameSliceCache.set(cacheKey, fallback);
      return fallback;
    }

    // Slices are measured from source edges to the detected transparent aperture.
    const detected = {
      top: Math.max(1, Math.min(topEdge, height - 1)),
      right: Math.max(1, Math.min(width - 1 - rightEdge, width - 1)),
      bottom: Math.max(1, Math.min(height - 1 - bottomEdge, height - 1)),
      left: Math.max(1, Math.min(leftEdge, width - 1)),
    };

    frameSliceCache.set(cacheKey, detected);
    return detected;
  }

  function parseConfig(configScript) {
    if (!configScript || !configScript.textContent) return null;
    try {
      return JSON.parse(configScript.textContent);
    } catch (error) {
      log('Failed to parse wall art config JSON.', error);
      return null;
    }
  }

  function getSectionIdFromConfigScript(configScript) {
    const id = configScript?.id || '';
    const prefix = 'WallArtGalleryRendererConfig-';
    if (!id.startsWith(prefix)) return null;
    return id.slice(prefix.length);
  }

  function findProductRoot(configScript, sectionId) {
    return (
      configScript.closest('product-info') ||
      document.querySelector(`product-info[data-section="${sectionId}"]`) ||
      document.getElementById(`MainProduct-${sectionId}`) ||
      null
    );
  }

  function findVariantSelects(productRoot) {
    return productRoot?.querySelector('variant-selects') || null;
  }

  function readVariantId(productRoot) {
    if (!productRoot) return null;
    const idInputs = productRoot.querySelectorAll('form input[name="id"]');
    for (const input of idInputs) {
      const parsed = Number.parseInt(input.value, 10);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
    return null;
  }

  function readSelectedVariantDataNode(variantSelects) {
    const node = variantSelects?.querySelector('script[data-selected-variant]');
    if (!node?.textContent) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (_error) {
      return null;
    }
  }

  function collectSelectedOptions(variantSelects) {
    const optionsByPosition = [];
    if (!variantSelects) return optionsByPosition;

    variantSelects.querySelectorAll('select').forEach((select) => {
      const selectedOption = select.selectedOptions && select.selectedOptions[0];
      const position = Number.parseInt(selectedOption?.dataset?.optionPosition || '', 10);
      if (!Number.isFinite(position) || position < 1) return;
      optionsByPosition[position - 1] = select.value;
    });

    variantSelects.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
      const position = Number.parseInt(input.dataset.optionPosition || '', 10);
      if (!Number.isFinite(position) || position < 1) return;
      optionsByPosition[position - 1] = input.value;
    });

    return optionsByPosition;
  }

  function findVariantById(variants, variantId) {
    if (!Array.isArray(variants) || !variants.length || !variantId) return null;
    const parsedId = Number.parseInt(variantId, 10);
    if (!Number.isFinite(parsedId)) return null;
    return variants.find((variant) => Number.parseInt(variant.id, 10) === parsedId) || null;
  }

  function findVariantBySelectedOptions(variants, selectedOptions) {
    if (!Array.isArray(variants) || !variants.length) return null;
    if (!Array.isArray(selectedOptions) || !selectedOptions.length) return null;

    return (
      variants.find((variant) => {
        const variantOptions = [variant.option1, variant.option2, variant.option3];
        for (let index = 0; index < selectedOptions.length; index += 1) {
          const selectedValue = selectedOptions[index];
          if (selectedValue == null || selectedValue === '') continue;
          if (String(variantOptions[index] ?? '') !== String(selectedValue)) return false;
        }
        return true;
      }) || null
    );
  }

  function resolveSelectedVariant(config, productRoot, variantSelects) {
    const variants = Array.isArray(config?.variants) ? config.variants : [];
    if (!variants.length) return null;

    const variantIdFromForm = readVariantId(productRoot);
    const variantFromId = findVariantById(variants, variantIdFromForm);
    if (variantFromId) return variantFromId;

    const selectedOptions = collectSelectedOptions(variantSelects);
    const variantFromOptions = findVariantBySelectedOptions(variants, selectedOptions);
    if (variantFromOptions) return variantFromOptions;

    const selectedVariantData = readSelectedVariantDataNode(variantSelects);
    const variantFromDataNode = findVariantById(variants, selectedVariantData?.id);
    if (variantFromDataNode) return variantFromDataNode;

    return variants.find((variant) => variant.available) || variants[0] || null;
  }

  function findGalleryElement(productRoot, sectionId) {
    return productRoot?.querySelector(`#MediaGallery-${sectionId}`) || productRoot?.querySelector('media-gallery') || null;
  }

  function findGallerySlotElements(productRoot, sectionId) {
    const mediaGallery = findGalleryElement(productRoot, sectionId);
    if (!mediaGallery) return [];

    return Array.from(mediaGallery.querySelectorAll(SLOT_SELECTORS)).sort((a, b) => {
      const aSlot = Number.parseInt(a.dataset.wallArtDynamicSlot || '', 10);
      const bSlot = Number.parseInt(b.dataset.wallArtDynamicSlot || '', 10);
      return aSlot - bSlot;
    });
  }

  function findHeroSlotElement(productRoot, sectionId) {
    const mediaGallery = findGalleryElement(productRoot, sectionId);
    if (!mediaGallery) return null;

    const galleryList =
      mediaGallery.querySelector(`#Slider-Gallery-${sectionId}`) ||
      mediaGallery.querySelector('[id^="Slider-Gallery-"]') ||
      mediaGallery;

    const heroCandidates = Array.from(galleryList.querySelectorAll('li.product__media-item[data-media-id]')).filter(
      (item) =>
        !item.hasAttribute('data-wall-art-dynamic-slot') &&
        !item.hasAttribute('data-wall-art-static-detail') &&
        !String(item.dataset.mediaId || '').includes('wall-art-slot')
    );

    return heroCandidates[0] || null;
  }

  function findHeroThumbnailElement(productRoot, sectionId) {
    const mediaGallery = findGalleryElement(productRoot, sectionId);
    if (!mediaGallery) return null;

    const thumbnailList =
      mediaGallery.querySelector(`#Slider-Thumbnails-${sectionId}`) ||
      mediaGallery.querySelector('[id^="Slider-Thumbnails-"]');
    if (!thumbnailList) return null;

    return (
      thumbnailList.querySelector('.thumbnail-list__item[data-media-position="1"]') ||
      thumbnailList.querySelector('.thumbnail-list__item')
    );
  }

  function findThumbnailSlotElement(productRoot, sectionId, slot) {
    const mediaGallery = findGalleryElement(productRoot, sectionId);
    if (!mediaGallery) return null;
    return mediaGallery.querySelector(`[data-wall-art-dynamic-thumb-slot="${slot}"]`);
  }

  function ensureWallArtDynamicThumb(thumbList, slot) {
    const existing = thumbList.querySelector(
      `.thumbnail-list__item[data-wall-art-dynamic-thumb-slot="${slot}"]`
    );
    if (existing) return existing;
    const candidate = thumbList.querySelector(
      `.thumbnail-list__item[data-media-position="${slot}"]:not([data-wall-art-static-detail])`
    );
    if (candidate) {
      candidate.setAttribute('data-wall-art-dynamic-thumb-slot', String(slot));
      return candidate;
    }
    return null;
  }

  function ensureWallArtStaticThumb(mediaGallery, thumbList, sectionId) {
    const mainStatic = mediaGallery.querySelector(
      'li.product__media-item[data-wall-art-static-detail="true"]'
    );
    if (!mainStatic) return null;
    const expectedTarget = mainStatic.dataset.mediaId || `${sectionId}-wall-art-slot-5`;

    let thumb = thumbList.querySelector(
      '.thumbnail-list__item[data-wall-art-static-detail="true"]'
    );
    if (!thumb) {
      thumb = thumbList.querySelector(
        `.thumbnail-list__item[data-target="${expectedTarget}"]`
      );
    }
    if (!thumb) {
      thumb = thumbList.querySelector(
        '.thumbnail-list__item[data-target$="wall-art-slot-5"]'
      );
    }
    if (!thumb) {
      thumb = thumbList.querySelector(
        '.thumbnail-list__item[data-media-position="5"]:not([data-wall-art-dynamic-thumb-slot])'
      );
    }
    if (!thumb) {
      const dyn4 = thumbList.querySelector(
        '.thumbnail-list__item[data-wall-art-dynamic-thumb-slot="4"]'
      );
      if (dyn4) {
        thumb = dyn4.cloneNode(true);
        thumb.removeAttribute('data-wall-art-dynamic-thumb-slot');
        thumb.id = `Slide-Thumbnails-${sectionId}-wall-art-slot-5`;
        const button = thumb.querySelector('button');
        if (button) {
          button.removeAttribute('aria-current');
        }
        dyn4.insertAdjacentElement('afterend', thumb);
      }
    }
    if (!thumb) return null;

    thumb.setAttribute('data-wall-art-static-detail', 'true');
    thumb.setAttribute('data-target', expectedTarget);
    thumb.setAttribute('data-media-position', '5');
    thumb.classList.remove('thumbnail-list_item--variant');
    return thumb;
  }

  function repositionWallArtThumbs(thumbList) {
    const dyn4 = thumbList.querySelector(
      '.thumbnail-list__item[data-wall-art-dynamic-thumb-slot="4"]'
    );
    const staticThumb = thumbList.querySelector(
      '.thumbnail-list__item[data-wall-art-static-detail="true"]'
    );
    if (dyn4 && staticThumb && dyn4.nextElementSibling !== staticThumb) {
      dyn4.insertAdjacentElement('afterend', staticThumb);
    }
  }

  function normalizeWallArtThumbnails(productRoot, sectionId) {
    const mediaGallery = findGalleryElement(productRoot, sectionId);
    if (!mediaGallery) return;
    const thumbList = mediaGallery.querySelector('[id^="GalleryThumbnails-"]');
    if (!thumbList) return;

    for (let slot = 2; slot <= 4; slot++) {
      const mainSlot = mediaGallery.querySelector(
        `li.product__media-item[data-wall-art-dynamic-slot="${slot}"]`
      );
      if (mainSlot) ensureWallArtDynamicThumb(thumbList, slot);
    }

    ensureWallArtStaticThumb(mediaGallery, thumbList, sectionId);
    repositionWallArtThumbs(thumbList);
  }

  function observeWallArtThumbnails(productRoot, sectionId, abortSignal) {
    const mediaGallery = findGalleryElement(productRoot, sectionId);
    if (!mediaGallery) return;
    const thumbList = mediaGallery.querySelector('[id^="GalleryThumbnails-"]');
    if (!thumbList) return;

    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        normalizeWallArtThumbnails(productRoot, sectionId);
      });
    });
    observer.observe(thumbList, { childList: true, subtree: false });

    if (abortSignal) {
      abortSignal.addEventListener('abort', () => observer.disconnect(), { once: true });
    }
  }

  function getSlotImages(slotElement) {
    if (!slotElement) return [];
    if (slotElement.tagName === 'IMG') return [slotElement];
    return Array.from(slotElement.querySelectorAll('img'));
  }

  function nextRenderToken(sectionId) {
    const current = globalState.renderTokens.get(sectionId) || 0;
    const next = current + 1;
    globalState.renderTokens.set(sectionId, next);
    return next;
  }

  function isLatestRenderToken(sectionId, token) {
    return (globalState.renderTokens.get(sectionId) || 0) === token;
  }

  function getDynamicSlotConfig(config, slot) {
    if (!Array.isArray(config?.dynamicSlots)) return null;
    return config.dynamicSlots.find((item) => Number.parseInt(item?.slot, 10) === Number.parseInt(slot, 10)) || null;
  }

  function getTemplateUrl(config, productType, backgroundKey, sizeGroup) {
    const normalizedSizeGroup = normalizeSizeGroup(sizeGroup) || 'medium';
    const templatesBySizeGroup = config?.templatesBySizeGroup;
    if (templatesBySizeGroup && typeof templatesBySizeGroup === 'object') {
      const sizeGroupTemplates = templatesBySizeGroup[normalizedSizeGroup];
      if (sizeGroupTemplates && typeof sizeGroupTemplates === 'object') {
        const templateUrlBySizeGroup = toAbsoluteUrl(sizeGroupTemplates[backgroundKey]);
        if (templateUrlBySizeGroup) return templateUrlBySizeGroup;
      }
    }

    // Backward compatibility for older config shape keyed by product type.
    const templates = config?.templates;
    if (!templates || typeof templates !== 'object') return '';
    const productTemplates = templates[productType];
    if (!productTemplates || typeof productTemplates !== 'object') return '';
    return toAbsoluteUrl(productTemplates[backgroundKey]);
  }

  function getFrameOverlayUrl(config, productType, frameColor) {
    if (!isFramedProductType(productType)) return '';

    const frames = config?.frames;
    if (!frames || typeof frames !== 'object') return '';

    const productFrames = frames[productType];
    if (!productFrames || typeof productFrames !== 'object') return '';

    return toAbsoluteUrl(productFrames[frameColor]);
  }

  function getFrameThickness(config, productType, sizeGroup) {
    if (!isFramedProductType(productType)) return 0;

    const normalizedSizeGroup = normalizeSizeGroup(sizeGroup) || 'medium';
    const groupedThicknessMap = config?.frameThicknessByProductTypeBySizeGroup;
    if (groupedThicknessMap && typeof groupedThicknessMap === 'object') {
      const groupedProductThickness = groupedThicknessMap[productType];
      if (groupedProductThickness && typeof groupedProductThickness === 'object') {
        const groupedValue = Number.parseFloat(groupedProductThickness[normalizedSizeGroup]);
        if (Number.isFinite(groupedValue) && groupedValue > 0) {
          return Math.max(1, Math.round(groupedValue));
        }

        const groupedMediumValue = Number.parseFloat(groupedProductThickness.medium);
        if (Number.isFinite(groupedMediumValue) && groupedMediumValue > 0) {
          return Math.max(1, Math.round(groupedMediumValue));
        }
      }
    }

    const configuredThickness =
      config?.frameThicknessByProductType && typeof config.frameThicknessByProductType === 'object'
        ? Number.parseFloat(config.frameThicknessByProductType[productType])
        : Number.NaN;

    if (Number.isFinite(configuredThickness) && configuredThickness > 0) {
      return Math.max(1, Math.round(configuredThickness));
    }

    return FRAME_THICKNESS_PX;
  }

  function getFramedArtHeroConfig(config) {
    const heroConfig = config?.framedArtPrintHero;
    const sourceBox = heroConfig?.artBox || DEFAULT_FRAMED_ART_HERO_BOX;

    const x = Number.parseFloat(sourceBox?.x);
    const y = Number.parseFloat(sourceBox?.y);
    const w = Number.parseFloat(sourceBox?.w);
    const h = Number.parseFloat(sourceBox?.h);

    const artBox =
      Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0
        ? { x, y, w, h }
        : { ...DEFAULT_FRAMED_ART_HERO_BOX };

    return {
      enabled: heroConfig?.enabled !== false,
      backgroundColor: String(heroConfig?.backgroundColor || '#ffffff').trim() || '#ffffff',
      artBox,
      frameThicknessBySize:
        heroConfig?.frameThicknessBySize && typeof heroConfig.frameThicknessBySize === 'object'
          ? heroConfig.frameThicknessBySize
          : null,
      frameThicknessBySizeGroup:
        heroConfig?.frameThicknessBySizeGroup && typeof heroConfig.frameThicknessBySizeGroup === 'object'
          ? heroConfig.frameThicknessBySizeGroup
          : null,
    };
  }

  function getFramedArtHeroFrameThickness(config, size, sizeGroup) {
    const normalizedSize = normalizeSize(size);
    const normalizedSizeGroup = normalizeSizeGroup(sizeGroup) || 'medium';
    const heroConfig = getFramedArtHeroConfig(config);
    const thicknessBySize = heroConfig.frameThicknessBySize;

    if (thicknessBySize && normalizedSize) {
      const directBySize = Number.parseFloat(thicknessBySize[normalizedSize]);
      if (Number.isFinite(directBySize) && directBySize > 0) {
        return Math.max(1, Math.round(directBySize));
      }
    }

    const groupedThickness = heroConfig.frameThicknessBySizeGroup;

    if (groupedThickness) {
      const direct = Number.parseFloat(groupedThickness[normalizedSizeGroup]);
      if (Number.isFinite(direct) && direct > 0) {
        return Math.max(1, Math.round(direct));
      }

      const medium = Number.parseFloat(groupedThickness.medium);
      if (Number.isFinite(medium) && medium > 0) {
        return Math.max(1, Math.round(medium));
      }
    }

    return getFrameThickness(config, 'framed_art_print', normalizedSizeGroup);
  }

  function parseSizeInches(sizeStr) {
    if (!sizeStr || typeof sizeStr !== 'string') return null;
    const parts = sizeStr.toLowerCase().replace(/["″\s]/g, '').split('x');
    if (parts.length !== 2) return null;
    const w = Number.parseFloat(parts[0]);
    const h = Number.parseFloat(parts[1]);
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
    return { w, h };
  }

  function getAnchorSize(config, sizeGroup) {
    const normalizedSizeGroup = normalizeSizeGroup(sizeGroup) || 'medium';
    const anchorSizesBySizeGroup = config?.anchorSizesBySizeGroup;
    if (anchorSizesBySizeGroup && typeof anchorSizesBySizeGroup === 'object') {
      const anchorSizeByGroup = anchorSizesBySizeGroup[normalizedSizeGroup];
      if (anchorSizeByGroup && typeof anchorSizeByGroup === 'object') {
        const w = Number.parseFloat(anchorSizeByGroup.w);
        const h = Number.parseFloat(anchorSizeByGroup.h);
        if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
          return { w, h };
        }
      }
    }

    const legacyAnchorSize = config?.anchorSize;
    const legacyW = Number.parseFloat(legacyAnchorSize?.w);
    const legacyH = Number.parseFloat(legacyAnchorSize?.h);
    if (Number.isFinite(legacyW) && Number.isFinite(legacyH) && legacyW > 0 && legacyH > 0) {
      return { w: legacyW, h: legacyH };
    }

    return { w: 24, h: 36 };
  }

  function getAnchorPlacement(config, backgroundKey, sizeGroup) {
    const normalizedSizeGroup = normalizeSizeGroup(sizeGroup) || 'medium';
    const anchorPlacementsBySizeGroup = config?.anchorPlacementsBySizeGroup;
    if (anchorPlacementsBySizeGroup && typeof anchorPlacementsBySizeGroup === 'object') {
      const groupPlacements = anchorPlacementsBySizeGroup[normalizedSizeGroup];
      if (groupPlacements && typeof groupPlacements === 'object') {
        const groupedAnchor = groupPlacements[backgroundKey];
        if (groupedAnchor && typeof groupedAnchor === 'object') {
          const x = Number.parseFloat(groupedAnchor.x);
          const y = Number.parseFloat(groupedAnchor.y);
          const w = Number.parseFloat(groupedAnchor.w);
          const h = Number.parseFloat(groupedAnchor.h);
          if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
            return { x, y, w, h };
          }
        }
      }
    }

    const anchors = config?.anchorPlacements;
    if (!anchors || typeof anchors !== 'object') return DEFAULT_ART_BOX;
    const anchor = anchors[backgroundKey];
    if (!anchor || typeof anchor !== 'object') return DEFAULT_ART_BOX;
    const x = Number.parseFloat(anchor.x);
    const y = Number.parseFloat(anchor.y);
    const w = Number.parseFloat(anchor.w);
    const h = Number.parseFloat(anchor.h);
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
      return DEFAULT_ART_BOX;
    }
    return { x, y, w, h };
  }

  function getPlacementBox(config, backgroundKey, sizeStr, sizeGroup) {
    const anchor = getAnchorPlacement(config, backgroundKey, sizeGroup);
    const variantSize = parseSizeInches(sizeStr);
    const anchorSize = getAnchorSize(config, sizeGroup);
    const refW = Number.parseFloat(anchorSize?.w) || 24;
    const refH = Number.parseFloat(anchorSize?.h) || 36;

    if (!variantSize) return anchor;

    const scaleW = variantSize.w / refW;
    const scaleH = variantSize.h / refH;
    const newW = anchor.w * scaleW;
    const newH = anchor.h * scaleH;
    const centerX = anchor.x + anchor.w / 2;
    const centerY = anchor.y + anchor.h / 2;
    return {
      x: centerX - newW / 2,
      y: centerY - newH / 2,
      w: newW,
      h: newH,
    };
  }

  function computeArtworkRect(maxBox, aspectRatio) {
    const safeRatio = Number.isFinite(aspectRatio) && aspectRatio > 0 ? aspectRatio : 2 / 3;

    let width = maxBox.w;
    let height = width / safeRatio;

    if (height > maxBox.h) {
      height = maxBox.h;
      width = height * safeRatio;
    }

    return {
      x: maxBox.x + (maxBox.w - width) / 2,
      y: maxBox.y + (maxBox.h - height) / 2,
      w: width,
      h: height,
      ratio: safeRatio,
    };
  }

  function resolveAspectRatio(variant, sizeValue) {
    const variantRatio = Number.parseFloat(variant?.aspectRatio);
    if (Number.isFinite(variantRatio) && variantRatio > 0) {
      return variantRatio;
    }

    const parsedRatio = parseSizeToRatio(sizeValue);
    if (Number.isFinite(parsedRatio) && parsedRatio > 0) {
      return parsedRatio;
    }

    return 2 / 3;
  }

  function drawProductShadow(ctx, rect, productType, config) {
    const isCanvasType = productType === 'canvas_print' || productType === 'framed_canvas_print';
    const isFramedType = productType === 'framed_art_print' || productType === 'framed_canvas_print';
    const shadowProfile = getShadowProfile(config, productType);
    const maxEdge = Math.max(rect.w, rect.h);
    const scaledDistance = Math.max(0, maxEdge * 0.018 * shadowProfile.distanceScale);
    const baseDistance = Math.max(0, Math.min(24 * shadowProfile.distanceScale, scaledDistance));
    if (shadowProfile.opacityScale <= 0 || baseDistance <= 0) return;

    // Ambient shadow to seat the product in the scene.
    ctx.save();
    const ambientOpacity = clampNumber((isFramedType ? 0.12 : 0.08) * shadowProfile.opacityScale, 0, 1);
    ctx.shadowColor = `rgba(0, 0, 0, ${ambientOpacity})`;
    ctx.shadowBlur = (isFramedType ? 18 : 14) * shadowProfile.blurScale;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = Math.max(0, baseDistance * 0.2);
    ctx.fillStyle = 'rgba(0, 0, 0, 1)';
    ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
    ctx.restore();

    // Directional cast shadow trail: multiple passes stretched along light direction.
    const castOpacity = clampNumber(
      (isCanvasType ? 0.26 : 0.22) * shadowProfile.opacityScale * shadowProfile.directionalStrengthScale,
      0,
      1
    );
    const castBlurBase = (isCanvasType ? 22 : 18) * shadowProfile.blurScale;
    const castTrailScale = shadowProfile.castTrailScale;
    const castPassCount = Math.round(clampNumber(4 + shadowProfile.blurScale * 4 * Math.max(0.4, castTrailScale), 4, 18));
    const directionalSpread = 1.15 * castTrailScale;

    for (let passIndex = 0; passIndex < castPassCount; passIndex += 1) {
      const t = (passIndex + 1) / castPassCount;
      const passOpacity = clampNumber((castOpacity * (1 - t * 0.65) * 1.9) / castPassCount, 0, 1);
      if (passOpacity <= 0) continue;

      ctx.save();
      ctx.shadowColor = `rgba(0, 0, 0, ${passOpacity})`;
      ctx.shadowBlur = castBlurBase * (0.4 + t * 0.8);
      ctx.shadowOffsetX = baseDistance * shadowProfile.directionX * (0.25 + t * directionalSpread);
      ctx.shadowOffsetY = baseDistance * shadowProfile.directionY * (0.25 + t * directionalSpread);
      ctx.fillStyle = 'rgba(0, 0, 0, 1)';
      ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
      ctx.restore();
    }

    // Tight contact pass near right/bottom edges for realism.
    ctx.save();
    const contactOpacity = clampNumber(0.18 * shadowProfile.opacityScale * shadowProfile.contactStrengthScale, 0, 1);
    ctx.shadowColor = `rgba(0, 0, 0, ${contactOpacity})`;
    ctx.shadowBlur = (isFramedType ? 10 : 8) * shadowProfile.blurScale;
    ctx.shadowOffsetX = baseDistance * shadowProfile.directionX * 0.56;
    ctx.shadowOffsetY = baseDistance * shadowProfile.directionY * 0.56;
    ctx.fillStyle = 'rgba(0, 0, 0, 1)';
    ctx.fillRect(rect.x + 2, rect.y + 2, rect.w - 4, rect.h - 4);
    ctx.restore();
  }

  function drawFallbackBackground(ctx, backgroundKey, productType) {
    const paletteByBackground = {
      bg1: ['#f5f3ef', '#eae5dd'],
      bg2: ['#eef3f6', '#e1eaf0'],
      bg3: ['#f4efe9', '#ebe2d7'],
    };
    const [startColor, endColor] = paletteByBackground[backgroundKey] || ['#f4f4f4', '#ebebeb'];
    const gradient = ctx.createLinearGradient(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
    gradient.addColorStop(0, startColor);
    gradient.addColorStop(1, endColor);

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

    const textureAlpha = productType === 'canvas_print' || productType === 'framed_canvas_print' ? 0.05 : 0.035;
    ctx.save();
    ctx.strokeStyle = `rgba(0, 0, 0, ${textureAlpha})`;
    ctx.lineWidth = 1;
    for (let y = 40; y < OUTPUT_SIZE; y += 120) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(OUTPUT_SIZE, y);
      ctx.stroke();
    }
    ctx.restore();
  }

  function drawArtworkSurface(ctx, artworkImage, rect, productType) {
    // Non-uniform stretch: ctx.drawImage(img, x, y, w, h) — fills the
    // target rect exactly without preserving source aspect. We feed
    // a single 2:3 source for ALL aspects (renderer-config.liquid
    // points every artworkUrlsByAspect entry at artwork_2x3_lowres.jpg
    // since that's the only lowres variant on GCS), so we must stretch
    // it into the variant's actual aspect (4:5 for 8x10/16x20, 3:4
    // for 18x24, 2:3 otherwise) instead of cover-fitting which would
    // crop the sides. Same `fit: fill` math image-processor.ts:41-45
    // already uses to generate the per-aspect masters from one source.
    ctx.drawImage(artworkImage, rect.x, rect.y, rect.w, rect.h);

    ctx.save();

    if (productType === 'art_print') {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.65)';
      ctx.lineWidth = 2;
      ctx.strokeRect(rect.x + 1, rect.y + 1, rect.w - 2, rect.h - 2);
    } else if (productType === 'canvas_print' || productType === 'framed_canvas_print') {
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.20)';
      ctx.lineWidth = 2;
      ctx.strokeRect(rect.x + 1, rect.y + 1, rect.w - 2, rect.h - 2);
    } else {
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.08)';
      ctx.lineWidth = 1;
      ctx.strokeRect(rect.x + 0.5, rect.y + 0.5, rect.w - 1, rect.h - 1);
    }

    ctx.restore();
  }

  async function renderDynamicMockup({
    config,
    variant,
    slot,
    backgroundKey,
    productType,
    frameColor,
    size,
    sizeGroup,
    templateVersion,
    aspectRatio,
    renderMode = 'dynamic_slot',
    forceWhiteBackground = false,
    transparentBackground = false,
    fixedPlacementBox = null,
    frameThicknessOverride = null,
    backgroundColor = '#ffffff',
    outputSize = OUTPUT_SIZE,
    outputWidth,
    outputHeight,
  }) {
    // Resolve canvas dims. If caller passed explicit width/height, use those
    // (they intend a non-square canvas matched to the print's aspect ratio,
    // so the wall-builder's slot↔composite alignment is pixel-exact).
    // Otherwise fall back to a square canvas of size outputSize.
    const canvasW = Number.isFinite(outputWidth) && outputWidth > 0 ? Math.round(outputWidth) : outputSize;
    const canvasH = Number.isFinite(outputHeight) && outputHeight > 0 ? Math.round(outputHeight) : outputSize;
    const productId = String(config.productId || '');
    const variantId = String(variant.id || '');

    const placementSig =
      fixedPlacementBox && Number.isFinite(fixedPlacementBox.w) && Number.isFinite(fixedPlacementBox.h)
        ? `fixed:${fixedPlacementBox.x},${fixedPlacementBox.y},${fixedPlacementBox.w},${fixedPlacementBox.h}`
        : getPlacementSignature(config, backgroundKey, sizeGroup);
    const shadowSig = getShadowSignature(config, productType);
    const configuredFrameThickness = Number.parseFloat(frameThicknessOverride);
    const frameThickness =
      Number.isFinite(configuredFrameThickness) && configuredFrameThickness > 0
        ? Math.max(1, Math.round(configuredFrameThickness))
        : getFrameThickness(config, productType, sizeGroup);
    log('Render request', {
      slot,
      backgroundKey,
      renderMode,
      size,
      sizeGroup,
      frameThickness,
      placementSig,
    });
    const renderCacheKey = getRenderCacheKey({
      productId,
      variantId,
      slot,
      backgroundKey,
      productType,
      frameColor,
      frameThickness,
      size,
      sizeGroup,
      templateVersion: `${templateVersion}|${renderMode}|tb${transparentBackground ? 1 : 0}|c${canvasW}x${canvasH}`,
      placementSig,
      shadowSig,
    });

    const cachedEntry = renderCache.get(renderCacheKey);
    if (cachedEntry?.blobUrl) {
      renderCache.delete(renderCacheKey);
      renderCache.set(renderCacheKey, cachedEntry);
      return {
        blobUrl: cachedEntry.blobUrl,
        cached: true,
        renderCacheKey,
      };
    }

    const artworkCandidateUrls = buildArtworkCandidateUrls(config, {
      variant,
      productType,
      size,
      aspectRatio,
    });
    if (!artworkCandidateUrls.length) {
      throw new Error('Missing artworkUrl in wall art config.');
    }

    // Skip loading background scene when caller wants either a flat color
    // (forceWhiteBackground) or a transparent canvas (transparentBackground —
    // used by the Gallery Wall Builder so each on-wall item is just frame +
    // artwork + shadow on alpha=0 over the wall canvas).
    const templateUrl = (forceWhiteBackground || transparentBackground) ? '' : getTemplateUrl(config, productType, backgroundKey, sizeGroup);
    const frameOverlayUrl = getFrameOverlayUrl(config, productType, frameColor);
    const shouldDrawFrameOverlay = isFramedProductType(productType);

    const templatePromise = templateUrl
      ? loadImage(templateUrl).catch((error) => {
          log('Background template failed to load, using generated fallback background.', { templateUrl, error });
          return null;
        })
      : Promise.resolve(null);
    const frameOverlayPromise =
      shouldDrawFrameOverlay && frameOverlayUrl
        ? loadImage(frameOverlayUrl).catch((error) => {
            log('Frame overlay failed to load; using artwork-only fallback.', { frameOverlayUrl, error });
            return null;
          })
        : Promise.resolve(null);

    const [{ image: artworkImage, usedUrl: usedArtworkUrl }, templateImage, frameOverlayImage] = await Promise.all([
      loadFirstAvailableImage(artworkCandidateUrls),
      templatePromise,
      frameOverlayPromise,
    ]);

    const drawMockupToCanvas = (activeArtworkImage) => {
      const canvas = document.createElement('canvas');
      canvas.width = canvasW;
      canvas.height = canvasH;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Could not initialize canvas rendering context.');
      }

      if (transparentBackground) {
        // Leave canvas alpha=0; the wall-builder canvas paints behind us.
      } else if (forceWhiteBackground) {
        ctx.fillStyle = backgroundColor || '#ffffff';
        ctx.fillRect(0, 0, canvasW, canvasH);
      } else if (templateImage) {
        drawImageCover(ctx, templateImage, 0, 0, canvasW, canvasH);
      } else {
        drawFallbackBackground(ctx, backgroundKey, productType);
      }

      const placementBox =
        fixedPlacementBox && Number.isFinite(fixedPlacementBox.w) && Number.isFinite(fixedPlacementBox.h)
          ? fixedPlacementBox
          : transparentBackground
            ? // Flat-mode default: center the artwork rect inside the canvas
              // with margin = frameThickness + shadowAllowance on each side.
              // Caller can pass fixedPlacementBox to align with a wall slot.
              (() => {
                const shadowAllowance = Math.round(Math.min(canvasW, canvasH) * 0.05);
                const m = frameThickness + shadowAllowance;
                return {
                  x: m,
                  y: m,
                  w: Math.max(1, canvasW - 2 * m),
                  h: Math.max(1, canvasH - 2 * m),
                };
              })()
            : getPlacementBox(config, backgroundKey, size, sizeGroup);
      const artworkRect = computeArtworkRect(placementBox, aspectRatio);
      const frameRect = {
        x: artworkRect.x - frameThickness,
        y: artworkRect.y - frameThickness,
        w: artworkRect.w + frameThickness * 2,
        h: artworkRect.h + frameThickness * 2,
      };
      const shadowRect = shouldDrawFrameOverlay && frameOverlayImage ? frameRect : artworkRect;

      drawProductShadow(ctx, shadowRect, productType, config);
      drawArtworkSurface(ctx, activeArtworkImage, artworkRect, productType);

      if (shouldDrawFrameOverlay) {
        if (frameOverlayImage) {
          const sourceWidth = frameOverlayImage.naturalWidth || frameOverlayImage.width;
          const sourceHeight = frameOverlayImage.naturalHeight || frameOverlayImage.height;
          const sourceSlices = getFrameSourceSlices(frameOverlayImage);

          drawNineSliceImage(
            ctx,
            frameOverlayImage,
            {
              x: 0,
              y: 0,
              w: sourceWidth,
              h: sourceHeight,
              sliceTop: sourceSlices.top,
              sliceRight: sourceSlices.right,
              sliceBottom: sourceSlices.bottom,
              sliceLeft: sourceSlices.left,
            },
            {
              x: frameRect.x,
              y: frameRect.y,
              w: frameRect.w,
              h: frameRect.h,
              sliceTop: frameThickness,
              sliceRight: frameThickness,
              sliceBottom: frameThickness,
              sliceLeft: frameThickness,
            }
          );
        } else if (frameOverlayUrl) {
          log('Frame overlay URL was provided but not rendered, using artwork-only fallback.', {
            productType,
            frameColor,
            frameOverlayUrl,
          });
        }
      }

      return canvas;
    };

    let blobUrl = '';
    try {
      blobUrl = await canvasToBlobUrl(drawMockupToCanvas(artworkImage));
    } catch (primaryRenderError) {
      const secondaryArtworkUrl = artworkCandidateUrls.find((url) => url !== usedArtworkUrl);
      if (!secondaryArtworkUrl) {
        throw primaryRenderError;
      }

      log('Primary artwork render failed, retrying with fallback artwork URL.', {
        usedArtworkUrl,
        secondaryArtworkUrl,
        primaryRenderError,
      });

      const secondaryArtworkImage = await loadImage(secondaryArtworkUrl);
      blobUrl = await canvasToBlobUrl(drawMockupToCanvas(secondaryArtworkImage));
    }

    setRenderCacheEntry(renderCacheKey, blobUrl);

    return {
      blobUrl,
      cached: false,
      renderCacheKey,
      usedArtworkUrl,
    };
  }

  function isBlobUrlReferencedInRenderCache(blobUrl) {
    if (!blobUrl) return false;

    for (const entry of renderCache.values()) {
      if (entry?.blobUrl === blobUrl) return true;
    }

    return false;
  }

  function maybeRevokeOldBlobUrl(img, nextBlobUrl) {
    const oldBlobUrl = img?.dataset?.wallArtBlobUrl;
    if (!oldBlobUrl || oldBlobUrl === nextBlobUrl) return;

    if (!isBlobUrlReferencedInRenderCache(oldBlobUrl)) {
      revokeOldBlobUrl(img);
    }
  }

  function buildDynamicAltText({ productTitle, productType, size, frame, backgroundKey }) {
    return `${productTitle} - ${productType} - ${size} - ${frame} - front mockup on background ${backgroundKey}`;
  }

  function applyBlobToImage(image, payload) {
    if (!image) return;

    const {
      blobUrl,
      variantId,
      productType,
      size,
      sizeGroup,
      frame,
      backgroundKey,
      altText,
    } = payload;

    maybeRevokeOldBlobUrl(image, blobUrl);

    if (image.getAttribute('src') !== blobUrl) {
      image.src = blobUrl;
    }

    if (image.hasAttribute('srcset')) {
      image.removeAttribute('srcset');
    }

    image.dataset.wallArtBlobUrl = blobUrl;
    image.dataset.wallArtSelectedVariantId = String(variantId || '');
    image.dataset.wallArtSelectedProductType = String(productType || '');
    image.dataset.wallArtSelectedSize = String(size || '');
    image.dataset.wallArtSelectedSizeGroup = String(sizeGroup || '');
    image.dataset.wallArtSelectedFrame = String(frame || '');
    image.dataset.wallArtRendered = 'true';
    image.dataset.wallArtBackgroundKey = String(backgroundKey || '');

    if (typeof altText === 'string' && altText) {
      image.alt = altText;
    }

    enforceSquareGalleryPresentation(image);
  }

  function isMainGalleryImage(image) {
    return Boolean(image?.closest('li.product__media-item[data-media-id]'));
  }

  function enforceSquareGalleryPresentation(image) {
    if (!(image instanceof HTMLImageElement) || !isMainGalleryImage(image)) return;

    const mediaContainer = image.closest('.product-media-container');
    if (mediaContainer) {
      mediaContainer.style.setProperty('--ratio', '1');
      mediaContainer.style.setProperty('--preview-ratio', '1');
    }

    image.setAttribute('width', String(OUTPUT_SIZE));
    image.setAttribute('height', String(OUTPUT_SIZE));
    image.style.aspectRatio = '1 / 1';
  }

  function preserveOriginalImageAttributes(image) {
    if (!(image instanceof HTMLImageElement)) return;

    const currentSrc = image.getAttribute('src') || '';
    const currentSrcSet = image.getAttribute('srcset') || '';
    const currentSizes = image.getAttribute('sizes') || '';
    const currentAlt = image.getAttribute('alt') || '';
    const currentWidth = image.getAttribute('width') || '';
    const currentHeight = image.getAttribute('height') || '';
    const currentAspectRatio = image.style.aspectRatio || '';
    const mediaContainer = image.closest('.product-media-container');
    const currentContainerRatio = mediaContainer?.style?.getPropertyValue('--ratio') || '';
    const currentContainerPreviewRatio = mediaContainer?.style?.getPropertyValue('--preview-ratio') || '';

    if (!currentSrc.startsWith('blob:') || !image.dataset.wallArtOriginalSrc) {
      image.dataset.wallArtOriginalSrc = currentSrc;
      image.dataset.wallArtOriginalSrcset = currentSrcSet;
      image.dataset.wallArtOriginalSizes = currentSizes;
      image.dataset.wallArtOriginalAlt = currentAlt;
      image.dataset.wallArtOriginalWidth = currentWidth;
      image.dataset.wallArtOriginalHeight = currentHeight;
      image.dataset.wallArtOriginalAspectRatio = currentAspectRatio;
      image.dataset.wallArtOriginalContainerRatio = currentContainerRatio;
      image.dataset.wallArtOriginalContainerPreviewRatio = currentContainerPreviewRatio;
    }
  }

  function restoreOriginalImageAttributes(image) {
    if (!(image instanceof HTMLImageElement)) return;

    const oldBlobUrl = image.dataset.wallArtBlobUrl;
    const originalSrc = image.dataset.wallArtOriginalSrc || '';
    const originalSrcset = image.dataset.wallArtOriginalSrcset || '';
    const originalSizes = image.dataset.wallArtOriginalSizes || '';
    const originalAlt = image.dataset.wallArtOriginalAlt || '';
    const originalWidth = image.dataset.wallArtOriginalWidth || '';
    const originalHeight = image.dataset.wallArtOriginalHeight || '';
    const originalAspectRatio = image.dataset.wallArtOriginalAspectRatio || '';
    const originalContainerRatio = image.dataset.wallArtOriginalContainerRatio || '';
    const originalContainerPreviewRatio = image.dataset.wallArtOriginalContainerPreviewRatio || '';

    if (originalSrc) {
      image.setAttribute('src', originalSrc);
    } else {
      image.removeAttribute('src');
    }

    if (originalSrcset) {
      image.setAttribute('srcset', originalSrcset);
    } else {
      image.removeAttribute('srcset');
    }

    if (originalSizes) {
      image.setAttribute('sizes', originalSizes);
    } else {
      image.removeAttribute('sizes');
    }

    image.setAttribute('alt', originalAlt);
    if (originalWidth) {
      image.setAttribute('width', originalWidth);
    } else {
      image.removeAttribute('width');
    }
    if (originalHeight) {
      image.setAttribute('height', originalHeight);
    } else {
      image.removeAttribute('height');
    }
    if (originalAspectRatio) {
      image.style.aspectRatio = originalAspectRatio;
    } else {
      image.style.removeProperty('aspect-ratio');
    }

    const mediaContainer = image.closest('.product-media-container');
    if (mediaContainer) {
      if (originalContainerRatio) {
        mediaContainer.style.setProperty('--ratio', originalContainerRatio);
      } else {
        mediaContainer.style.removeProperty('--ratio');
      }
      if (originalContainerPreviewRatio) {
        mediaContainer.style.setProperty('--preview-ratio', originalContainerPreviewRatio);
      } else {
        mediaContainer.style.removeProperty('--preview-ratio');
      }
    }

    delete image.dataset.wallArtRendered;
    delete image.dataset.wallArtBackgroundKey;
    delete image.dataset.wallArtSelectedVariantId;
    delete image.dataset.wallArtSelectedProductType;
    delete image.dataset.wallArtSelectedSize;
    delete image.dataset.wallArtSelectedSizeGroup;
    delete image.dataset.wallArtSelectedFrame;
    delete image.dataset.wallArtFramedArtHero;
    delete image.dataset.wallArtBlobUrl;
    delete image.dataset.wallArtOriginalWidth;
    delete image.dataset.wallArtOriginalHeight;
    delete image.dataset.wallArtOriginalAspectRatio;
    delete image.dataset.wallArtOriginalContainerRatio;
    delete image.dataset.wallArtOriginalContainerPreviewRatio;

    if (oldBlobUrl && !isBlobUrlReferencedInRenderCache(oldBlobUrl) && !isBlobUrlUsedInDocument(oldBlobUrl)) {
      revokeBlobUrl(oldBlobUrl);
    }
  }

  function applySelectionMetadataToSlotImages(slotElements, metadata) {
    slotElements.forEach((slotElement) => {
      const slotImages = getSlotImages(slotElement);
      slotImages.forEach((image) => {
        image.dataset.wallArtSelectedVariantId = metadata.variantId;
        image.dataset.wallArtSelectedProductType = metadata.productType;
        image.dataset.wallArtSelectedSize = metadata.size;
        image.dataset.wallArtSelectedSizeGroup = metadata.sizeGroup;
        image.dataset.wallArtSelectedFrame = metadata.frame;
      });
    });
  }

  function setWallArtRenderingState(slotElement, thumbSlotElement, isRendering) {
    const targets = [slotElement, thumbSlotElement];
    targets.forEach((target) => {
      if (!target || !target.classList) return;
      target.classList.toggle('wall-art-rendering', isRendering);
      const images = getSlotImages(target);
      images.forEach((image) => {
        image.classList.toggle('wall-art-rendering', isRendering);
      });
    });
  }

  function clearFramedArtHeroOverride(productRoot, sectionId) {
    const heroSlotElement = findHeroSlotElement(productRoot, sectionId);
    if (!heroSlotElement) return;

    const heroImages = getSlotImages(heroSlotElement);
    heroImages.forEach((image) => {
      if (image.dataset.wallArtFramedArtHero === 'true') {
        restoreOriginalImageAttributes(image);
      }
    });
  }

  async function renderFramedArtHeroSlot({
    config,
    productRoot,
    sectionId,
    renderToken,
    variant,
    frameColor,
    size,
    sizeGroup,
    templateVersion,
    aspectRatio,
    productTitle,
  }) {
    const heroConfig = getFramedArtHeroConfig(config);
    if (!heroConfig.enabled) return;

    const heroSlotElement = findHeroSlotElement(productRoot, sectionId);
    if (!heroSlotElement) return;

    const heroImages = getSlotImages(heroSlotElement);
    if (!heroImages.length) return;

    heroImages.forEach((image) => preserveOriginalImageAttributes(image));
    setWallArtRenderingState(heroSlotElement, null, true);

    try {
      if (!isLatestRenderToken(sectionId, renderToken)) return;

      const heroFrameThickness = getFramedArtHeroFrameThickness(config, size, sizeGroup);
      const result = await renderDynamicMockup({
        config,
        variant,
        slot: 1,
        backgroundKey: 'hero',
        productType: 'framed_art_print',
        frameColor,
        size,
        sizeGroup,
        templateVersion,
        aspectRatio,
        renderMode: 'framed_art_hero_slot_1',
        forceWhiteBackground: true,
        fixedPlacementBox: heroConfig.artBox,
        frameThicknessOverride: heroFrameThickness,
        backgroundColor: heroConfig.backgroundColor,
      });

      if (!isLatestRenderToken(sectionId, renderToken)) return;

      const altText = `${productTitle} - framed_art_print - ${size} - ${frameColor} - hero mockup`;
      const payload = {
        blobUrl: result.blobUrl,
        variantId: String(variant.id || ''),
        productType: 'framed_art_print',
        size,
        sizeGroup,
        frame: frameColor,
        backgroundKey: 'hero',
        altText,
      };

      heroImages.forEach((image) => {
        applyBlobToImage(image, payload);
        image.dataset.wallArtFramedArtHero = 'true';
      });

      log('Rendered framed art print hero slot.', {
        sectionId,
        variantId: variant.id,
        sizeGroup,
        frameThickness: heroFrameThickness,
        cached: result.cached,
      });
    } catch (error) {
      log('Failed rendering framed art print hero slot; keeping static fallback.', {
        sectionId,
        variantId: variant.id,
        error,
      });
    } finally {
      setWallArtRenderingState(heroSlotElement, null, false);
    }
  }

  async function renderSlotsForVariant({ config, productRoot, sectionId, variant }) {
    const slotElements = findGallerySlotElements(productRoot, sectionId);
    if (!slotElements.length) return;

    const renderToken = nextRenderToken(sectionId);

    const productType = normalizeProductType(variant.productType || config.productType);
    let frameColor = normalizeFrameColor(variant.frame);
    if (productType === 'art_print' || productType === 'canvas_print') {
      frameColor = 'unframed';
    }

    const selectedSize = normalizeSize(variant.size);
    const selectedSizeGroup = getSizeGroupForSize(selectedSize, config);
    const variantId = String(variant.id || '').trim();
    const productTitle = String(config.productTitle || '').trim();
    const templateVersion = String(config.templateVersion || 'v1');

    const sizeAllowed = isAllowedSizeForProductType(productType, selectedSize, config);
    if (!sizeAllowed) {
      log('Selected size is outside allowedSizesByProductType; rendering with parsed/default ratio.', {
        variantId,
        productType,
        selectedSize,
      });
    }

    log('Resolved size group', {
      sectionId,
      variantId,
      size: selectedSize,
      sizeGroup: selectedSizeGroup,
    });

    applySelectionMetadataToSlotImages(slotElements, {
      variantId,
      productType,
      size: selectedSize,
      sizeGroup: selectedSizeGroup,
      frame: frameColor,
    });
    const slotAspectRatio = resolveAspectRatio(variant, selectedSize);

    // Slot 1 (the hero) is owned by `wall-art-hero-swap.js` for ALL product
    // types now — it reads the design's `custom.hero_images_json` metafield
    // (server-rendered on the bulk pipeline) and swaps the listing image's
    // <img src + alt> on each variant change. The previous framed_art_print
    // browser-canvas override is no longer needed; we just clear any
    // residual override attributes left by older versions of this script.
    clearFramedArtHeroOverride(productRoot, sectionId);

    if (!isLatestRenderToken(sectionId, renderToken)) return;

    const slotEntries = slotElements
      .map((slotElement) => {
        const slot = Number.parseInt(slotElement.dataset.wallArtDynamicSlot || '', 10);
        if (!Number.isFinite(slot)) return null;

        const dynamicSlotConfig = getDynamicSlotConfig(config, slot);
        const backgroundKey = dynamicSlotConfig?.backgroundKey || `bg${slot - 1}`;

        return {
          slotElement,
          slot,
          backgroundKey,
        };
      })
      .filter(Boolean);

    const renderSingleSlot = async ({ slotElement, slot, backgroundKey }) => {
      const thumbSlotElement = findThumbnailSlotElement(productRoot, sectionId, slot);
      setWallArtRenderingState(slotElement, thumbSlotElement, true);

      try {
        if (!isLatestRenderToken(sectionId, renderToken)) return;

        const result = await renderDynamicMockup({
          config,
          variant,
          slot,
          backgroundKey,
          productType,
          frameColor,
          size: selectedSize,
          sizeGroup: selectedSizeGroup,
          templateVersion,
          aspectRatio: slotAspectRatio,
        });

        if (!isLatestRenderToken(sectionId, renderToken)) return;

        const altText = buildDynamicAltText({
          productTitle,
          productType,
          size: selectedSize,
          frame: frameColor,
          backgroundKey,
        });

        const applyPayload = {
          blobUrl: result.blobUrl,
          variantId,
          productType,
          size: selectedSize,
          sizeGroup: selectedSizeGroup,
          frame: frameColor,
          backgroundKey,
          altText,
        };

        const slotImages = getSlotImages(slotElement);
        if (!slotImages.length) return;
        slotImages.forEach((image) => applyBlobToImage(image, applyPayload));

        if (thumbSlotElement) {
          const thumbImages = getSlotImages(thumbSlotElement);
          if (thumbImages.length) {
            thumbImages.forEach((image) => applyBlobToImage(image, applyPayload));
          }
        }

        log('Rendered dynamic slot.', {
          sectionId,
          variantId,
          slot,
          backgroundKey,
          cached: result.cached,
        });
      } catch (error) {
        log('Failed rendering dynamic slot; keeping static fallback.', {
          sectionId,
          variantId,
          slot,
          backgroundKey,
          error,
        });
      } finally {
        setWallArtRenderingState(slotElement, thumbSlotElement, false);
      }
    };

    const immediateSlotEntry = slotEntries.find((entry) => entry.slot === 2) || null;
    const deferredSlotEntries = slotEntries.filter((entry) => entry.slot !== 2);

    if (immediateSlotEntry) {
      await renderSingleSlot(immediateSlotEntry);
    }

    if (deferredSlotEntries.length) {
      runWhenIdle(() => Promise.all(deferredSlotEntries.map((entry) => renderSingleSlot(entry)))).catch(() => {
        // Fail silently in production mode.
      });
    }
  }

  function initRendererForConfig(configScript) {
    const sectionId = getSectionIdFromConfigScript(configScript);
    if (!sectionId) return;

    const existingInstance = globalState.sectionInstances.get(sectionId);
    if (existingInstance && existingInstance.configScript === configScript && existingInstance.productRoot?.isConnected) {
      return;
    }
    if (existingInstance && typeof existingInstance.destroy === 'function') {
      existingInstance.destroy('reinit');
    }

    const config = parseConfig(configScript);
    if (!config || config.enabled === false) return;

    const productRoot = findProductRoot(configScript, sectionId);
    if (!productRoot) return;

    const initialSlots = findGallerySlotElements(productRoot, sectionId);
    if (!initialSlots.length) return;

    normalizeWallArtThumbnails(productRoot, sectionId);

    const abortController = new AbortController();
    observeWallArtThumbnails(productRoot, sectionId, abortController.signal);
    const variantSelects = findVariantSelects(productRoot);
    const cleanupFns = [];
    let isDestroyed = false;

    // ── Wall-art picker bridge (Orders-API architecture) ───────────────────
    //
    // Under the new architecture, format/size/frame_color are NOT Shopify
    // variants — they're picker state managed by <wall-art-picker>. The
    // renderer's resolveSelectedVariant() can't reach this state by itself,
    // so we listen for the picker's `wallart:selection:changed` event and
    // synthesize a virtual variant from its detail. updateSelectedVariantState
    // (below) will then prefer that virtual variant over anything in
    // variant-selects.
    let pickerVirtualVariant = null;
    const buildPickerVariant = (detail) => {
      if (!detail) return null;
      return {
        id: detail.variantId ?? `wap-${detail.format}-${detail.size}-${detail.frameColor}`,
        productType: detail.format,
        size: detail.size,
        frame: detail.frameColor === 'none' ? '' : detail.frameColor,
        option1: detail.size,
        option2: detail.frameColor === 'none' ? null : detail.frameColor,
        option3: null,
        available: true,
      };
    };
    const handlePickerChange = (event) => {
      const detail = event?.detail;
      pickerVirtualVariant = buildPickerVariant(detail);
      if (pickerVirtualVariant) debouncedVariantUpdate();
    };
    // Seed from any pre-existing picker state (handles race: picker may have
    // already dispatched its initial selection before this listener is bound,
    // since both scripts load with `defer`).
    if (window.__wallArtCurrentSelection) {
      pickerVirtualVariant = buildPickerVariant(window.__wallArtCurrentSelection);
    }
    productRoot.addEventListener('wallart:selection:changed', handlePickerChange, {
      signal: abortController.signal,
    });
    // Also catch events that bubble to document (the picker itself bubbles).
    document.addEventListener('wallart:selection:changed', handlePickerChange, {
      signal: abortController.signal,
    });

    const updateSelectedVariantState = () => {
      if (isDestroyed) return;

      normalizeWallArtThumbnails(productRoot, sectionId);

      // Prefer the picker's virtual variant if one has been emitted.
      if (pickerVirtualVariant) {
        Promise.resolve(
          renderSlotsForVariant({
            config,
            productRoot,
            sectionId,
            variant: pickerVirtualVariant,
          })
        ).catch(() => {});
        return;
      }

      const activeVariantSelects = findVariantSelects(productRoot) || variantSelects;
      const selectedVariant = resolveSelectedVariant(config, productRoot, activeVariantSelects);
      if (!selectedVariant) return;

      Promise.resolve(
        renderSlotsForVariant({
          config,
          productRoot,
          sectionId,
          variant: selectedVariant,
        })
      ).catch(() => {
        // Fail silently in production mode.
      });

      log('Variant update', {
        sectionId,
        variantId: selectedVariant.id,
        dynamicSlots: initialSlots.map((slot) => slot.dataset.wallArtDynamicSlot),
      });
    };

    const debouncedVariantUpdate = createDebouncedFunction(updateSelectedVariantState, VARIANT_CHANGE_DEBOUNCE_MS);
    cleanupFns.push(() => debouncedVariantUpdate.cancel());

    const destroy = (reason = 'manual') => {
      if (isDestroyed) return;
      isDestroyed = true;

      abortController.abort();
      while (cleanupFns.length) {
        const cleanup = cleanupFns.pop();
        try {
          cleanup();
        } catch (_error) {
          // Ignore cleanup failures.
        }
      }

      if (configScript?.dataset) {
        delete configScript.dataset.wallArtRendererInitialized;
      }
      if (productRoot?.dataset) {
        delete productRoot.dataset.wallArtRendererInitialized;
      }

      if (globalState.sectionInstances.get(sectionId)?.destroy === destroy) {
        globalState.sectionInstances.delete(sectionId);
      }

      log('Destroyed', { sectionId, reason });
    };

    configScript.dataset.wallArtRendererInitialized = 'true';
    productRoot.dataset.wallArtRendererInitialized = 'true';

    updateSelectedVariantState();
    productRoot.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const isVariantInput =
        target.matches('variant-selects select') ||
        target.matches('variant-selects input[type="radio"]') ||
        target.matches('form input[name="id"]');
      if (!isVariantInput) return;

      debouncedVariantUpdate();
    }, { signal: abortController.signal });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      const unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
        if (String(payload?.data?.sectionId ?? '') !== String(sectionId)) return;
        debouncedVariantUpdate();
      });
      cleanupFns.push(() => {
        if (typeof unsubscribeVariantChange === 'function') {
          unsubscribeVariantChange();
        }
      });
    }

    globalState.sectionInstances.set(sectionId, {
      sectionId,
      configScript,
      productRoot,
      destroy,
    });

    log('Initialized', {
      sectionId,
      productId: config.productId,
      dynamicSlotCount: initialSlots.length,
    });
  }

  function destroySectionInstance(sectionId, reason = 'manual') {
    const existingInstance = globalState.sectionInstances.get(sectionId);
    if (!existingInstance || typeof existingInstance.destroy !== 'function') return;
    existingInstance.destroy(reason);
  }

  function initAllRenderers(root = document) {
    const configScripts = new Set();
    if (root instanceof Element && root.matches(CONFIG_SELECTOR)) {
      configScripts.add(root);
    }
    if (root && typeof root.querySelectorAll === 'function') {
      root.querySelectorAll(CONFIG_SELECTOR).forEach((script) => configScripts.add(script));
    }

    configScripts.forEach((configScript) => {
      try {
        initRendererForConfig(configScript);
      } catch (_error) {
        // Fail silently in production mode.
      }
    });
  }

  // PERF_LCP: defer the gallery renderer's scene compositing off the critical path.
  // Slot 1 (the LCP hero) is now server-rendered as an eager <img>, so the renderer
  // only enhances slots 2-4 + wires format switching — both fine to run at idle,
  // just after first paint, so this canvas work doesn't delay the hero's paint.
  function scheduleRendererInit() {
    var ran = false;
    var run = function () {
      if (ran) return;
      ran = true;
      initAllRenderers();
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 2000 });
    } else {
      setTimeout(run, 250);
    }
    // requestIdleCallback only fires when the main thread is idle — but the
    // instant a customer starts swiping the gallery, the thread is busy with
    // touch/scroll and the idle callback gets starved toward its 2s timeout,
    // leaving slots 2-4 as un-composited hero placeholders exactly mid-swipe.
    // Kick the composite on the FIRST interaction so an early swiper triggers
    // it immediately instead of waiting out the timeout. initAllRenderers is
    // idempotent (guarded per-section), so the later idle call is a no-op.
    ['pointerdown', 'touchstart', 'keydown'].forEach(function (evt) {
      window.addEventListener(evt, run, { once: true, passive: true, capture: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleRendererInit, { once: true });
  } else {
    scheduleRendererInit();
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const thumbItem = target.closest(
      '.thumbnail-list__item[data-wall-art-dynamic-thumb-slot], .thumbnail-list__item[data-wall-art-static-detail], .thumbnail-list__item[data-wall-art-static-detail-slot]'
    );
    if (!thumbItem) return;

    const dynamicSlot = thumbItem.getAttribute('data-wall-art-dynamic-thumb-slot');
    const isStaticDetail = thumbItem.getAttribute('data-wall-art-static-detail') === 'true';
    const staticDetailSlot = thumbItem.getAttribute('data-wall-art-static-detail-slot');
    const slotSuffix = dynamicSlot || staticDetailSlot || (isStaticDetail ? '5' : null);
    if (!slotSuffix) return;

    const mediaGallery = thumbItem.closest('media-gallery');
    const viewer = mediaGallery?.querySelector('[id^="GalleryViewer-"]');
    const thumbnails = mediaGallery?.querySelector('[id^="GalleryThumbnails-"]');
    if (!viewer) return;

    let activeMedia = null;
    if (dynamicSlot) {
      activeMedia = viewer.querySelector(
        `li.product__media-item[data-wall-art-dynamic-slot="${dynamicSlot}"]`
      );
    } else if (isStaticDetail) {
      activeMedia = viewer.querySelector(
        'li.product__media-item[data-wall-art-static-detail="true"]'
      );
    }
    if (!activeMedia) {
      activeMedia = viewer.querySelector(
        `li.product__media-item[data-media-id$="wall-art-slot-${slotSuffix}"]`
      );
    }
    if (!activeMedia) return;

    viewer
      .querySelectorAll('li.product__media-item[data-media-id]')
      .forEach((el) => el.classList.remove('is-active'));
    activeMedia.classList.add('is-active');

    if (thumbnails) {
      thumbnails
        .querySelectorAll('.thumbnail-list__item button[aria-current]')
        .forEach((btn) => btn.removeAttribute('aria-current'));
      const activeButton = thumbItem.querySelector('button');
      if (activeButton) activeButton.setAttribute('aria-current', 'true');
    }
  });

  document.addEventListener('shopify:section:load', (event) => {
    const sectionRoot = event?.target instanceof Element ? event.target : document;
    initAllRenderers(sectionRoot);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const sectionId = String(event?.detail?.sectionId || '').trim();
    if (sectionId) {
      destroySectionInstance(sectionId, 'section-unload');
      return;
    }

    const sectionRoot = event?.target instanceof Element ? event.target : null;
    if (!sectionRoot) return;

    const configScript = sectionRoot.matches?.(CONFIG_SELECTOR)
      ? sectionRoot
      : sectionRoot.querySelector?.(CONFIG_SELECTOR);
    const derivedSectionId = getSectionIdFromConfigScript(configScript);
    if (derivedSectionId) {
      destroySectionInstance(derivedSectionId, 'section-unload');
    }
  });

  // ──────────────────────────────────────────────────────────────────
  // Public API — window.WallArtCompositor
  //
  // Exposes the same compositing pipeline that renders product-page
  // slot 2/3/4 to outside callers (currently: the Gallery Wall Builder
  // bundle). Two modes:
  //   - renderScene(...) — full lifestyle scene with background template,
  //     placement anchors, etc. (existing slot 2/3/4 behavior; not used
  //     in v1 but exposed for symmetry)
  //   - renderFlat(...) — frame + artwork + drop-shadow on a transparent
  //     canvas. Used by the wall builder so each on-wall item is a flat
  //     composite that the builder positions over its own wall canvas.
  //
  // Implementation: synthesizes a minimal `variant`-shaped object from
  // the caller's inputs and calls `renderDynamicMockup`. Reuses the
  // module's `parseConfig`, frame asset map, shadow profiles, and LRU
  // render cache.
  // ──────────────────────────────────────────────────────────────────

  let cachedDefaultConfig = null;
  function getDefaultConfig() {
    if (cachedDefaultConfig) return cachedDefaultConfig;
    const script = document.querySelector(CONFIG_SELECTOR);
    if (!script) return null;
    cachedDefaultConfig = parseConfig(script);
    return cachedDefaultConfig;
  }

  function synthVariant({ designId, designArtworkUrlsByAspect, productType, size, frameColor }) {
    // Public API uses customer-facing aspect keys ('2x3'/'3x4'/'4x5');
    // the renderer's internal helpers (resolveArtworkAspectKey,
    // buildArtworkCandidateUrls) expect underscore form ('2_3'/'3_4'/'4_5').
    // Translate at the API boundary so callers don't have to know.
    const inputUrls = designArtworkUrlsByAspect || {};
    const internalUrls = {};
    if (inputUrls['2x3'] || inputUrls['2_3']) internalUrls['2_3'] = inputUrls['2x3'] || inputUrls['2_3'];
    if (inputUrls['3x4'] || inputUrls['3_4']) internalUrls['3_4'] = inputUrls['3x4'] || inputUrls['3_4'];
    if (inputUrls['4x5'] || inputUrls['4_5']) internalUrls['4_5'] = inputUrls['4x5'] || inputUrls['4_5'];
    return {
      id: `flat:${designId || 'anon'}:${productType}:${size}:${frameColor}`,
      artworkUrlsByAspect: internalUrls,
      productType,
      size,
      frameColor,
      sizeGroup: '',
      aspectRatio: undefined,
    };
  }

  /**
   * Render a flat (transparent-background) composite of artwork+frame+shadow.
   * Returns a promise resolving to { blobUrl, cached, aspectRatio, width, height }.
   */
  async function renderFlat(input) {
    const {
      designId,
      designArtworkUrlsByAspect,
      productType,
      size,
      frameColor = 'none',
      outputSize = 800,
      outputWidth,
      outputHeight,
      placementBox,
      configOverride,
    } = input || {};

    const config = configOverride || getDefaultConfig();
    if (!config) {
      throw new Error('WallArtCompositor.renderFlat: no WallArtGalleryRendererConfig-* script found on page.');
    }
    if (!designArtworkUrlsByAspect || typeof designArtworkUrlsByAspect !== 'object') {
      throw new Error('WallArtCompositor.renderFlat: designArtworkUrlsByAspect is required.');
    }

    const variant = synthVariant({ designId, designArtworkUrlsByAspect, productType, size, frameColor });
    const aspectKey = resolveArtworkAspectKey({ variant, size, aspectRatio: undefined });
    const aspectRatio = parseSizeToRatio(size) || (aspectKey === '4_5' ? 4 / 5 : aspectKey === '3_4' ? 3 / 4 : 2 / 3);

    const cw = Number.isFinite(outputWidth) && outputWidth > 0 ? outputWidth : outputSize;
    const ch = Number.isFinite(outputHeight) && outputHeight > 0 ? outputHeight : outputSize;

    const result = await renderDynamicMockup({
      config: { ...config, productId: config.productId || 'flat-compositor' },
      variant,
      slot: 'flat',
      backgroundKey: 'flat',
      productType,
      frameColor,
      size,
      sizeGroup: '',
      templateVersion: String(config.templateVersion || ''),
      aspectRatio,
      renderMode: 'flat',
      transparentBackground: true,
      outputSize,
      outputWidth: cw,
      outputHeight: ch,
      fixedPlacementBox: placementBox || null,
    });

    return {
      blobUrl: result.blobUrl,
      cached: result.cached,
      aspectRatio,
      width: cw,
      height: ch,
    };
  }

  /**
   * Render a full lifestyle-scene composite (the existing slot 2/3/4 recipe).
   * Exposed for parity with renderFlat; not used by the wall builder in v1.
   */
  async function renderScene(input) {
    const {
      designId,
      designArtworkUrlsByAspect,
      productType,
      size,
      frameColor = 'none',
      slot = 2,
      backgroundKey = 'living_room',
      configOverride,
      outputSize,
    } = input || {};

    const config = configOverride || getDefaultConfig();
    if (!config) {
      throw new Error('WallArtCompositor.renderScene: no WallArtGalleryRendererConfig-* script found on page.');
    }

    const variant = synthVariant({ designId, designArtworkUrlsByAspect, productType, size, frameColor });
    const aspectKey = resolveArtworkAspectKey({ variant, size, aspectRatio: undefined });
    const aspectRatio = parseSizeToRatio(size) || (aspectKey === '4_5' ? 4 / 5 : aspectKey === '3_4' ? 3 / 4 : 2 / 3);

    const result = await renderDynamicMockup({
      config: { ...config, productId: config.productId || 'scene-compositor' },
      variant,
      slot,
      backgroundKey,
      productType,
      frameColor,
      size,
      sizeGroup: '',
      templateVersion: String(config.templateVersion || ''),
      aspectRatio,
      renderMode: 'scene',
      ...(typeof outputSize === 'number' ? { outputSize } : {}),
    });

    return {
      blobUrl: result.blobUrl,
      cached: result.cached,
      aspectRatio,
    };
  }

  // Expose. Idempotent — if the script reloads in dev, the latest
  // implementation wins.
  window.WallArtCompositor = {
    renderFlat,
    renderScene,
    /** Returns the current default config (parsed once and cached). */
    getDefaultConfig,
  };
})();
